import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { 
  Users as UsersIcon,
  Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Team() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('team').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {team.length} {t('teamMembers').toLowerCase()}
          </p>
        </div>
      </div>

      {/* Team Grid */}
      {team.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('noDataAvailable')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {team.map((member) => (
            <div 
              key={member.id} 
              className="border border-border rounded-lg p-4 bg-card hover:border-primary transition-colors"
              data-testid={`team-member-${member.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-lg">
                    {member.first_name} {member.last_name}
                  </h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Mail className="h-3 w-3" />
                    {member.email}
                  </p>
                </div>
                <Badge className={`${member.is_active ? 'bg-emerald-500' : 'bg-red-500'} text-white rounded`}>
                  {member.is_active ? t('active') : t('inactive')}
                </Badge>
              </div>
              
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {member.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
